<script setup lang="ts">
import { defineEmits, defineProps, h, onMounted, ref } from 'vue'
import { NButton, useDialog, useMessage } from 'naive-ui'
import columnsApprovedAccount from './columnsApprovedAccount'
import { getApprovedAccount, updateApprovedAccount } from '@/api/workspace'
import { AppTable } from '@/components/common'
import { WORKSPACE } from '@/constants'
import { t } from '@/locales'

const props = defineProps({
  show: { type: Boolean, default: false },
  workspaceId: { type: Number, required: true },
})

const emit = defineEmits(['onClose'])

const message = useMessage()
const dialog = useDialog()

const showModal = ref<boolean>(props.show)
const loading = ref<boolean>(false)
const dataApprovedAccount = ref<any[]>([])

const closeModal = (reload: boolean) => {
  showModal.value = false
  emit('onClose', reload)
}

const handleClose = () => {
  closeModal(false)
}

const getDataApprovedAccount = async () => {
  try {
    loading.value = true
    const { data } = await getApprovedAccount({ workspace_id: props.workspaceId })
    if (data.success)
      dataApprovedAccount.value = data?.data?.items || []
  }
  finally {
    loading.value = false
  }
}

const updateStatus = async (row: any, type: any) => {
  try {
    loading.value = true
    const { data } = await updateApprovedAccount({ id: [row?.id], type })
    if (data?.success) {
      message.success(data.message)
      await getDataApprovedAccount()
    }
    else {
      message.error(data.message)
    }
  }
  finally {
    loading.value = false
  }
  dialog.destroyAll()
}

const approveAll = async () => {
  try {
    if (!dataApprovedAccount.value.length)
      return
    loading.value = true
    const ids = dataApprovedAccount.value.map((i: any) => i.id)
    const { data } = await updateApprovedAccount({ id: ids, type: WORKSPACE.STATUS.ACTION })
    if (data?.success) {
      message.success(data.message)
      await getDataApprovedAccount()
    }
    else {
      message.error(data?.message)
    }
  }
  finally {
    loading.value = false
  }
}

const onCancelUpdateStatus = () => {
  dialog.destroyAll()
}

const onAgree = (row: any, type: string) => {
  const title = t('settings.userManagementTab.titleApprove')
  const content: any = t('settings.userManagementTab.contentApprove')

  dialog.warning({
    title,
    showIcon: false,
    autoFocus: false,
    maskClosable: false,
    closable: false,
    content,
    contentClass: 'text-center',
    class: '!rounded-2xl !max-w-[368px]',
    action: () =>
      h(
        'div',
        { style: 'display: flex; gap: 12px; justify-content: flex-end; width: 100%' },
        [
          h(NButton, { size: 'medium', ghost: true, class: 'min-w-32 !w-1/2', type: 'primary', onClick: () => onCancelUpdateStatus() }, { default: () => t('common.cancel') }),
          h(NButton, { size: 'medium', class: 'min-w-32 !w-1/2', onClick: () => updateStatus(row, type), type: 'primary' }, { default: () => t('settings.userManagementTab.approve') }),
        ],
      ),
  })
}

const onReject = (row: any, type: string) => {
  const title = t('settings.userManagementTab.titleReject')
  const content: any = t('settings.userManagementTab.contentReject')

  dialog.warning({
    title,
    showIcon: false,
    autoFocus: false,
    maskClosable: false,
    closable: false,
    content,
    contentClass: 'text-center',
    class: '!rounded-2xl !max-w-[368px]',
    action: () =>
      h(
        'div',
        { style: 'display: flex; gap: 12px; justify-content: flex-end;width: 100%' },
        [
          h(NButton, { size: 'medium', ghost: true, class: 'min-w-32 !w-1/2', type: 'primary', onClick: () => onCancelUpdateStatus() }, { default: () => t('common.cancel') }),
          h(NButton, { size: 'medium', ghost: true, class: 'min-w-32 !w-1/2', type: 'error', onClick: () => updateStatus(row, type) }, { default: () => t('common.reject') }),
        ],
      ),
  })
}

const renderCell = (value: any, rowData: any, column: any) => {
  switch (column.key) {
    case 'index':
      return (dataApprovedAccount.value.indexOf(rowData) + 1)
    case 'actions':
      return h(
        'div',
        { class: 'flex items-center justify-center gap-4' },
        [
          h(NButton, { text: true, type: 'primary', onClick: () => updateStatus(rowData, WORKSPACE.STATUS.ACTION) }, { default: () => t('common.agree') }),
          h(NButton, { text: true, type: 'error', onClick: () => updateStatus(rowData, WORKSPACE.STATUS.INACTION) }, { default: () => t('common.reject') }),
        ],
      )
    default:
      return value
  }
}

onMounted(() => {
  getDataApprovedAccount()
})
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 920px;max-width: 100%"
    class="!rounded-2xl"
    content-class="!px-0 !py-0"
    header-class="!px-6 !pt-6 !pb-3 text-center"
    footer-class="!px-6 !pb-6 !pt-3 !w-full"
    @esc="handleClose"
    @mask-click="handleClose"
    @after-leave="handleClose"
  >
    <template #header>
      <div class="text-xl font-semibold">
        {{ $t('settings.userManagementTab.requestTitle') }}
      </div>
      <div class="mt-1 text-gray-500 text-sm font-normal" v-html="$t('settings.userManagementTab.requestDescription')" />
    </template>

    <div class="px-6 pb-2 w-full">
      <AppTable
        :columns="columnsApprovedAccount"
        :data-source="dataApprovedAccount"
        :show-header="false"
        :show-header-content="false"
        :show-header-footer="false"
        :show-quick-search="false"
        :loading="loading"
        :render-cell="renderCell"
        :show-card="false"
        class-table="custom-table custom-table-rounded custom-table-shadow"
      />
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <AppButton ghost size="medium" type="primary" class=" min-w-44" @click="handleClose">
          {{ $t('common.close') }}
        </AppButton>
        <AppButton size="medium" type="primary" class="min-w-44" :disabled="dataApprovedAccount.length === 0" @click="approveAll">
          {{ $t('common.addAll') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped></style>
